import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Alert,
  Autocomplete,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  LinearProgress,
  Paper,
  Skeleton,
  Stack,
  Typography,
  TextField,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  AssessmentOutlined as AssessmentOutlinedIcon,
  CalendarMonthOutlined as CalendarMonthOutlinedIcon,
  CheckCircleOutlineRounded as CheckCircleOutlineRoundedIcon,
  DnsOutlined as DnsOutlinedIcon,
  DirectionsCarFilledOutlined as DirectionsCarFilledOutlinedIcon,
  DownloadRounded as DownloadRoundedIcon,
  ErrorOutlineRounded as ErrorOutlineRoundedIcon,
  Groups2Outlined as Groups2OutlinedIcon,
  LocalShippingOutlined as LocalShippingOutlinedIcon,
  LaptopChromebookOutlined as LaptopChromebookOutlinedIcon,
  HelpOutlineRounded as HelpOutlineRoundedIcon,
  HistoryOutlined as HistoryOutlinedIcon,
  LogoutRounded as LogoutRoundedIcon,
  MenuRounded as MenuRoundedIcon,
  NotificationsActiveOutlined as NotificationsActiveOutlinedIcon,
  PersonOutlineRounded as PersonOutlineRoundedIcon,
  PlaceOutlined as PlaceOutlinedIcon,
  ReceiptLongOutlined as ReceiptLongOutlinedIcon,
  RouteOutlined as RouteOutlinedIcon,
  RefreshRounded as RefreshRoundedIcon,
  SettingsOutlined as SettingsOutlinedIcon,
  SpeedOutlined as SpeedOutlinedIcon,
  WarningAmberRounded as WarningAmberRoundedIcon,
} from "@mui/icons-material";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip as ReTooltip,
  XAxis,
  YAxis,
} from "recharts";
import { resolveStatus } from "../../utils/vehicleStatus";
import { logout } from "../../store/authSlice";
import { useSocket } from "../../hooks/useSocket";
import { navigateToAdminDashboard, readDashboardTabFromSearch } from "../../router/dashboardNavigation";
import { useGetDashboardSummaryQuery, useGetAllUsersQuery } from "../../redux/admin/dashboardApi";
import { useGetAllLiteDevicesQuery } from "../../redux/admin/adminApi";
import { useGetTripsQuery, useGetLicensesQuery } from "../../redux/logistics/logisticsApi";
import { useGetAllGroupsQuery } from "../../redux/admin/groupApi";
import { useGetUserProfileQuery } from "../../redux/admin/userApi";
import { ROLES, getPermissions, getRoleDisplayName } from "../../utils/rbacHelper";
import { getDemoReadOnlyMessage, isDemoReadOnlyUser } from "../../utils/demoUser";
import GlobalDashboardSidebar from "./GlobalDashboardSidebar";
import CreditBalanceIndicator from "./CreditBalanceIndicator";
import useGlobalSidebarItems from "./useGlobalSidebar";
import "./dashboard-redesign.css";

const safeArr = (value) => (Array.isArray(value) ? value : []);
const toLower = (value) => String(value || "").toLowerCase();
const toSafeCount = (value) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : 0;
};
const formatCount = (value) =>
  new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(Number(value || 0));
const formatNumber = (value) =>
  new Intl.NumberFormat("en-IN", { maximumFractionDigits: 2 }).format(Number(value || 0));
const formatCompactNumber = (value) =>
  new Intl.NumberFormat("en-IN", {
    notation: "compact",
    maximumFractionDigits: 1,
  }).format(Number(value || 0));
const formatDateRange = (start, end) => {
  const formatter = new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
  return `${formatter.format(start)} - ${formatter.format(end)}`;
};
const formatDateLabel = (value) =>
  new Intl.DateTimeFormat("en-GB", { day: "2-digit", month: "short" }).format(value);
const formatDateTime = (value) => {
  try {
    return value ? new Date(value).toLocaleString() : "—";
  } catch {
    return "—";
  }
};
const pickRole = (profile) =>
  profile?.role || profile?.user?.role || profile?.data?.role || ROLES.VIEWER;
const toTimeMs = (value) => {
  const parsed = new Date(value).getTime();
  return Number.isFinite(parsed) ? parsed : 0;
};
const formatRelativeTime = (value) => {
  const ts = toTimeMs(value);
  if (!ts) return "—";
  const diffMs = Math.max(0, Date.now() - ts);
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return formatDateTime(value);
};
const getRoleLabel = (role) => getRoleDisplayName(role) || String(role || "User").replace(/_/g, " ");
const getTripVehicleId = (trip) =>
  String(
    trip?.vehicle?._id ||
      trip?.vehicle?.id ||
      trip?.vehicleId?._id ||
      trip?.vehicleId ||
      trip?.device?._id ||
      trip?.device ||
      trip?.asset?._id ||
      trip?.asset ||
      ""
  ).trim();
const isOpenTrip = (trip) => {
  const tripStatus = String(trip?.tripStatus || "").trim().toLowerCase();
  const legacyStatus = String(trip?.status || "").trim().toLowerCase();
  const closed = new Set(["completed", "cancelled"]);
  if (tripStatus) return !closed.has(tripStatus);
  if (legacyStatus) return !closed.has(legacyStatus);
  return Boolean(trip?.startDate || trip?.routeName || trip?.vehicle);
};
const getPurchasedLicenseCount = (user) => Number(user?.licenseSummary?.totalAssignedCount ?? 0) || 0;
const getRemainingLicenseCount = (user) =>
  Number(user?.licenseSummary?.totalRemainingCredits ?? user?.remainingTokens ?? 0) || 0;
const getUsedLicenseCount = (user) =>
  Math.max(0, getPurchasedLicenseCount(user) - getRemainingLicenseCount(user));

const API_BASE =
  process.env.REACT_APP_BACKEND_URL ||
  process.env.REACT_APP_API_URL ||
  "https://app.krush.in/api";
const UPDATE_FEED_LIMIT = 12;
const ACTIVITY_FEED_ENDPOINTS = ["/notificationswebapp/user", "/notifications/user"];
const STATUS_META = {
  all: { label: "Total Vehicles", tone: "neutral", icon: <DirectionsCarFilledOutlinedIcon fontSize="small" /> },
  running: { label: "Running", tone: "success", icon: <DirectionsCarFilledOutlinedIcon fontSize="small" /> },
  idling: { label: "Idling", tone: "warning", icon: <DirectionsCarFilledOutlinedIcon fontSize="small" /> },
  stopped: { label: "Stopped", tone: "danger", icon: <DirectionsCarFilledOutlinedIcon fontSize="small" /> },
  noReporting: { label: "No Reporting", tone: "info", icon: <LaptopChromebookOutlinedIcon fontSize="small" /> },
  expired: { label: "Expired", tone: "violet", icon: <CalendarMonthOutlinedIcon fontSize="small" /> },
  noData: { label: "No Data", tone: "muted", icon: <DnsOutlinedIcon fontSize="small" /> },
};
const STATUS_COLORS = {
  running: "#22c55e",
  idling: "#f59e0b",
  stopped: "#ef4444",
  noReporting: "#0ea5e9",
  expired: "#8b5cf6",
  noData: "#94a3b8",
};

const getAuthToken = () => localStorage.getItem("token") || localStorage.getItem("authToken") || "";

const normalizeSystemNotification = (item = {}) => ({
  _id: item._id || item.notificationId || `system-${item.createdAt || Date.now()}`,
  title: item.title || "Notification",
  message: item.message || item.body || "",
  read: Boolean(item.read),
  type: item.eventType || item.type || "info",
  eventType: item.eventType || item.type || "info",
  source: item.source || "system",
  createdAt: item.createdAt || new Date().toISOString(),
  data: item.data || {},
});

const pickFirstText = (...values) => {
  for (const value of values) {
    if (value === null || value === undefined) continue;
    const text = String(value).trim();
    if (text) return text;
  }
  return "";
};

const normalizeActivityRow = (item = {}) => {
  const notification = normalizeSystemNotification(item);
  const data = notification.data || {};
  const actorName =
    pickFirstText(
      data.actorName,
      data.updatedByName,
      data.performedByName,
      data.createdByName,
      data.username,
      data.userName
    ) || "System";
  const itemName =
    pickFirstText(
      data.deviceName,
      data.vehicleName,
      data.entityName,
      data.targetName,
      data.routeName,
      data.groupName,
      data.name,
      data.imei,
      data.deviceId
    ) || "General update";
  const actionText = pickFirstText(notification.title, notification.eventType, notification.type, "Updated");
  return {
    id: String(notification._id),
    actorName,
    itemName,
    actionText,
    message: pickFirstText(notification.message, notification.title) || "Update received",
    createdAt: notification.createdAt || new Date().toISOString(),
    relativeTime: formatRelativeTime(notification.createdAt),
  };
};

function DashboardStatusCard({ label, value, detail, tone, icon, onClick, isFirst = false, isLast = false }) {
  return (
    <button
      className={`dashboard-status-card tone-${tone} ${isFirst ? "is-first" : ""} ${isLast ? "is-last" : ""}`}
      type="button"
      onClick={onClick}
    >
      <div className={`dashboard-status-card__icon tone-${tone}`}>
        {icon}
      </div>
      <div className="dashboard-status-card__content">
        <span className="dashboard-status-card__label">{label}</span>
        <strong className="dashboard-status-card__value">{formatCount(value)}</strong>
        <span className="dashboard-status-card__detail">{detail}</span>
      </div>
    </button>
  );
}

function DashboardTile({ icon, label, value, hint, tone = "neutral" }) {
  return (
    <div className={`dashboard-mini-tile tone-${tone}`}>
      <div className="dashboard-mini-tile__icon">{icon}</div>
      <div>
        <div className="dashboard-mini-tile__value">{value}</div>
        <div className="dashboard-mini-tile__label">{label}</div>
        {hint ? <div className="dashboard-mini-tile__hint">{hint}</div> : null}
      </div>
    </div>
  );
}

function DashboardSection({ title, action, children, className = "" }) {
  return (
    <Paper className={`dashboard-section ${className}`} elevation={0}>
      <div className="dashboard-section__header">
        <Typography variant="h6" className="dashboard-section__title">
          {title}
        </Typography>
        {action}
      </div>
      <div className="dashboard-section__body">{children}</div>
    </Paper>
  );
}

function EmptyCardState({ title, description }) {
  return (
    <div className="dashboard-empty-state">
      <div className="dashboard-empty-state__icon">
        <AssessmentOutlinedIcon />
      </div>
      <strong>{title}</strong>
      <span>{description}</span>
    </div>
  );
}

export default function Dashboard() {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const currentUser = useSelector((state) => state.auth.user);
  const theme = useTheme();
  const isLgDown = useMediaQuery(theme.breakpoints.down("lg"));
  const isMdDown = useMediaQuery(theme.breakpoints.down("md"));
  const { socket } = useSocket();
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [activityRows, setActivityRows] = useState([]);
  const [activityLoading, setActivityLoading] = useState(false);
  const [chartMode, setChartMode] = useState("distance");
  const [selectedVehicleId, setSelectedVehicleId] = useState("");
  const [vehicleSearchInput, setVehicleSearchInput] = useState("");

  const { data: profile, isLoading: profileLoading, refetch: refetchProfile } = useGetUserProfileQuery();
  const isDemoReadOnly = isDemoReadOnlyUser(currentUser || profile);
  const demoReadOnlyMessage = getDemoReadOnlyMessage(currentUser || profile);
  const role = pickRole(profile);
  const permissions = useMemo(() => getPermissions(role), [role]);
  const canSeeUsers = Boolean(permissions.viewUsers);

  const { data: dashboardSummary, isLoading: summaryLoading, refetch: refetchSummary } =
    useGetDashboardSummaryQuery(undefined, { skip: !permissions.viewDevices });
  const { data: devicesRaw, isLoading: devicesLoading, refetch: refetchDevices } =
    useGetAllLiteDevicesQuery({ includeRelations: "true" }, { skip: !permissions.viewDevices });
  const { data: tripsRaw, isLoading: tripsLoading, refetch: refetchTrips } =
    useGetTripsQuery(undefined, { skip: !permissions.viewDashboard });
  const { data: allUsersRaw, isLoading: usersLoading, refetch: refetchUsers } =
    useGetAllUsersQuery(undefined, { skip: !canSeeUsers });
  const { isLoading: licensesLoading, refetch: refetchLicenses } =
    useGetLicensesQuery(undefined, { skip: !canSeeUsers });
  const { data: groupsRaw, isLoading: groupsLoading, refetch: refetchGroups } =
    useGetAllGroupsQuery({ page: 1, limit: 500 }, { skip: !canSeeUsers });

  const devices = useMemo(() => safeArr(devicesRaw), [devicesRaw]);
  const trips = useMemo(() => safeArr(tripsRaw), [tripsRaw]);
  const users = useMemo(() => safeArr(allUsersRaw), [allUsersRaw]);
  const groups = useMemo(() => safeArr(groupsRaw?.data || groupsRaw), [groupsRaw]);

  const vehicleOptions = useMemo(() =>
    devices.map((device) => ({
      id: String(device?._id || device?.id || "").trim(),
      label: device?.vehicleNumber || device?.name || device?.deviceId || device?.imei || "Unknown vehicle",
      vehicleNumber: device?.vehicleNumber || "",
      name: device?.name || "",
      imei: device?.imei || device?.deviceId || "",
      searchText: [device?.vehicleNumber, device?.name, device?.imei, device?.deviceId].filter(Boolean).join(" ").toLowerCase(),
    })).filter((option) => option.id),
  [devices]);

  const selectedVehicleOption = useMemo(() =>
    vehicleOptions.find((option) => option.id === selectedVehicleId) || null,
  [selectedVehicleId, vehicleOptions]);

  useEffect(() => {
    if (selectedVehicleId && !vehicleOptions.some((option) => option.id === selectedVehicleId)) {
      setSelectedVehicleId("");
    }
  }, [selectedVehicleId, vehicleOptions]);

  useEffect(() => {
    const query = String(vehicleSearchInput || "").trim().toLowerCase();
    if (!query) return;
    const exact = vehicleOptions.find((option) =>
      [option.label, option.vehicleNumber, option.name, option.imei]
        .filter(Boolean)
        .some((value) => String(value).trim().toLowerCase() === query)
    );
    if (exact && exact.id !== selectedVehicleId) {
      setSelectedVehicleId(exact.id);
    }
  }, [selectedVehicleId, vehicleOptions, vehicleSearchInput]);

  useEffect(() => {
    if (selectedVehicleOption) {
      setVehicleSearchInput(selectedVehicleOption.label);
    }
  }, [selectedVehicleOption]);

  const filteredDevices = useMemo(() =>
    selectedVehicleId
      ? devices.filter((device) => String(device?._id || device?.id || "").trim() === selectedVehicleId)
      : devices,
    [devices, selectedVehicleId]
  );

  const filteredTrips = useMemo(() =>
    selectedVehicleId
      ? trips.filter((trip) => getTripVehicleId(trip) === selectedVehicleId)
      : trips,
    [selectedVehicleId, trips]
  );

  const roleCounts = useMemo(() => {
    const map = new Map();
    users.forEach((user) => {
      const key = String(user?.role || "").trim();
      if (!key) return;
      map.set(key, (map.get(key) || 0) + 1);
    });
    return map;
  }, [users]);

  const vehicleStats = useMemo(() => {
    const fallback = { all: filteredDevices.length, running: 0, idling: 0, stopped: 0, noReporting: 0, expired: 0, noData: 0 };
    filteredDevices.forEach((device) => {
      const status = toLower(resolveStatus(device));
      if (status === "running" || status === "moving") fallback.running += 1;
      else if (status === "idling" || status === "idle") fallback.idling += 1;
      else if (status === "stopped" || status === "parked") fallback.stopped += 1;
      else if (status === "no-reporting" || status === "offline" || status === "inactive") fallback.noReporting += 1;
      else if (status === "expired") fallback.expired += 1;
      else fallback.noData += 1;
    });

    const counts = dashboardSummary?.counts || {};
    const total = toSafeCount(counts.total);
    if (selectedVehicleId || !total) return fallback;

    const mergedTotal = Math.max(total, fallback.all);
    const mergedRunning = fallback.running;
    const mergedIdling = fallback.idling;
    const mergedNoReporting = Math.max(fallback.noReporting, toSafeCount(counts.inactive));
    const mergedExpired = Math.max(fallback.expired, toSafeCount(counts.expired));
    const mergedNoData = Math.max(fallback.noData, toSafeCount(counts.no_data));
    const mergedStopped = Math.max(
      fallback.stopped,
      mergedTotal - mergedRunning - mergedIdling - mergedNoReporting - mergedExpired - mergedNoData
    );

    return {
      all: mergedTotal,
      running: mergedRunning,
      idling: mergedIdling,
      stopped: mergedStopped,
      noReporting: mergedNoReporting,
      expired: mergedExpired,
      noData: mergedNoData,
    };
  }, [dashboardSummary?.counts, filteredDevices, selectedVehicleId]);

  const scopedDeviceIdSet = useMemo(
    () => new Set(filteredDevices.map((device) => String(device?._id || device?.id || "").trim()).filter(Boolean)),
    [filteredDevices]
  );

  const tripStatus = useMemo(() => {
    const vehiclesWithTrips = new Set();
    filteredTrips.forEach((trip) => {
      if (!isOpenTrip(trip)) return;
      const vehicleId = getTripVehicleId(trip);
      if (!vehicleId) return;
      if (!scopedDeviceIdSet.size || scopedDeviceIdSet.has(vehicleId)) {
        vehiclesWithTrips.add(vehicleId);
      }
    });
    const onTrip = vehiclesWithTrips.size;
    const fleetTotal = vehicleStats.all || filteredDevices.length;
    return {
      onTrip,
      noTrip: Math.max(0, fleetTotal - onTrip),
    };
  }, [filteredDevices.length, filteredTrips, scopedDeviceIdSet, vehicleStats.all]);

  const overviewSeries = useMemo(() => {
    return Array.from({ length: 7 }).map((_, index) => {
      const day = new Date();
      day.setHours(0, 0, 0, 0);
      day.setDate(day.getDate() - (6 - index));

      const dayTrips = filteredTrips.filter((trip) => {
        const when = new Date(trip?.startDate || trip?.createdAt || trip?.date || 0);
        return when.toDateString() === day.toDateString();
      }).length;

      const dayDistance = filteredDevices.reduce((sum, device) => {
        const history = safeArr(device?.dailyDistanceHistory || device?.distanceHistory);
        const matched = history.find((item) => {
          const itemDate = new Date(item?.date || item?.day || item?.timestamp || 0);
          return itemDate.toDateString() === day.toDateString();
        });
        if (matched) return sum + Number(matched?.distance || matched?.value || 0);
        if (index === 6) return sum + Number(device?.dailyDistance || 0);
        return sum;
      }, 0);

      return {
        label: formatDateLabel(day),
        distance: Math.round(dayDistance),
        trips: dayTrips,
      };
    });
  }, [filteredDevices, filteredTrips]);

  const vehiclePieData = useMemo(
    () =>
      Object.entries(STATUS_META)
        .filter(([key]) => key !== "all")
        .map(([key, meta]) => ({
          key,
          name: meta.label,
          value: Number(vehicleStats[key] || 0),
          color: STATUS_COLORS[key],
        }))
        .filter((item) => item.value > 0),
    [vehicleStats]
  );

  const topOdometerDevices = useMemo(
    () =>
      [...filteredDevices]
        .filter((device) => Number(device?.odometer ?? device?.totalOdo ?? device?.totalDistance ?? 0) > 0)
        .sort(
          (left, right) =>
            Number(right?.odometer ?? right?.totalOdo ?? right?.totalDistance ?? 0) -
            Number(left?.odometer ?? left?.totalOdo ?? left?.totalDistance ?? 0)
        )
        .slice(0, 10)
        .map((device) => ({
          id: String(device?._id || device?.id || device?.imei || device?.deviceId || Math.random()),
          label: device?.vehicleNumber || device?.name || device?.deviceId || device?.imei || "Unknown vehicle",
          value: Number(device?.odometer ?? device?.totalOdo ?? device?.totalDistance ?? 0),
        })),
    [filteredDevices]
  );

  const licenseSummary = useMemo(
    () =>
      users.reduce(
        (acc, user) => {
          acc.purchased += getPurchasedLicenseCount(user);
          acc.used += getUsedLicenseCount(user);
          acc.remaining += getRemainingLicenseCount(user);
          return acc;
        },
        { purchased: 0, used: 0, remaining: 0 }
      ),
    [users]
  );

  const sidebarItemsLegacy = useMemo(
    () => [
      { key: "dashboard", label: "Dashboard", icon: <AssessmentOutlinedIcon />, active: true, onClick: () => navigate("/dashboard") },
      {
        key: "vehicles",
        label: "Vehicles",
        icon: <DirectionsCarFilledOutlinedIcon />,
        onClick: () => navigateToAdminDashboard(navigate, { tab: "devices" }),
      },
      {
        key: "map",
        label: "Map",
        icon: <PlaceOutlinedIcon />,
        active: location.pathname === "/admin-dashboard" && readDashboardTabFromSearch(location.search, "assignments") === "map",
        defaultExpanded: location.pathname === "/admin-dashboard" && readDashboardTabFromSearch(location.search, "assignments") === "map",
        children: [
          { key: "map-live-tracking", label: "Live Tracking", active: location.pathname === "/admin-dashboard" && readDashboardTabFromSearch(location.search, "assignments") === "map", onClick: () => navigateToAdminDashboard(navigate, { tab: "map" }) },
        ],
      },
      {
        key: "history",
        label: "History",
        icon: <HistoryOutlinedIcon />,
        active: location.pathname === "/admin-dashboard" && ["history_playback", "history_logs", "history_events", "history_parking"].includes(readDashboardTabFromSearch(location.search, "assignments")),
        defaultExpanded: true,
        children: [
          { key: "history-playback", label: "Playback", active: readDashboardTabFromSearch(location.search, "assignments") === "history_playback", onClick: () => navigateToAdminDashboard(navigate, { tab: "history_playback" }) },
          { key: "history-logs", label: "Logs", active: readDashboardTabFromSearch(location.search, "assignments") === "history_logs", onClick: () => navigateToAdminDashboard(navigate, { tab: "history_logs" }) },
          { key: "history-events", label: "Events", active: readDashboardTabFromSearch(location.search, "assignments") === "history_events", onClick: () => navigateToAdminDashboard(navigate, { tab: "history_events" }) },
          { key: "history-parking", label: "Parking", active: readDashboardTabFromSearch(location.search, "assignments") === "history_parking", onClick: () => navigateToAdminDashboard(navigate, { tab: "history_parking" }) },
        ],
      },
      { key: "trips", label: "Trips", icon: <RouteOutlinedIcon />, onClick: () => navigate("/logistic/trips") },
      {
        key: "geofences",
        label: "Geofences",
        icon: <PlaceOutlinedIcon />,
        active: location.pathname === "/geofences",
        onClick: () => navigate("/geofences"),
      },
      {
        key: "notifications",
        label: "Notifications",
        icon: <NotificationsActiveOutlinedIcon />,
        active: location.pathname === "/notifications",
        onClick: () => navigate("/notifications"),
      },
      { key: "reports", label: "Reports", icon: <ReceiptLongOutlinedIcon />, onClick: () => navigate("/reports") },
      {
        key: "logistic",
        label: "Logistic",
        icon: <LocalShippingOutlinedIcon />,
        active: location.pathname.startsWith("/logistic"),
        children: [
          { key: "logistics-trips", label: "Trips", active: location.pathname === "/logistic/trips", onClick: () => navigate("/logistic/trips") },
          { key: "logistics-clients", label: "Clients", active: location.pathname === "/logistic/clients", onClick: () => navigate("/logistic/clients") },
          { key: "logistics-materials", label: "Materials", active: location.pathname === "/logistic/materials", onClick: () => navigate("/logistic/materials") },
          { key: "logistics-documents", label: "Documents", active: location.pathname === "/logistic/documents", onClick: () => navigate("/logistic/documents") },
        ],
      },
      { key: "groups", label: "Groups", icon: <Groups2OutlinedIcon />, onClick: () => navigate("/groups") },
      {
        key: "users",
        label: "Users & Roles",
        icon: <PersonOutlineRoundedIcon />,
        onClick: () => navigateToAdminDashboard(navigate, { tab: "users" }),
      },
      {
        key: "billing",
        label: "Billing & Licenses",
        icon: <AssessmentOutlinedIcon />,
        active: location.pathname === "/licenses",
        children: [
          { key: "licenses", label: "Licenses", active: location.pathname === "/licenses" && !location.search.includes("section="), onClick: () => navigate("/licenses") },
          { key: "all-licenses", label: "All Licenses", active: location.pathname === "/licenses" && location.search.includes("section=all-licenses"), onClick: () => navigate("/licenses?section=all-licenses") },
          { key: "purchase-license", label: "Purchase License", active: location.pathname === "/licenses" && location.search.includes("section=purchase-license"), onClick: () => navigate("/licenses?section=purchase-license") },
          { key: "license-summary", label: "License Summary", active: location.pathname === "/licenses" && location.search.includes("section=license-summary"), onClick: () => navigate("/licenses?section=license-summary") },
          { key: "transactions", label: "Transactions", active: location.pathname === "/licenses" && location.search.includes("section=transactions"), onClick: () => navigate("/licenses?section=transactions") },
        ],
      },
      { key: "settings", label: "Settings", icon: <SettingsOutlinedIcon />, onClick: () => navigate("/profile") },
    ],
    [location.pathname, location.search, navigate]
  );
  const sidebarItems = useGlobalSidebarItems();
  void sidebarItemsLegacy;

  const fetchActivityFeed = useCallback(async () => {
    if (!permissions.viewDevices && !permissions.viewDashboard && !canSeeUsers) {
      setActivityRows([]);
      return;
    }
    const token = getAuthToken();
    if (!token) {
      setActivityRows([]);
      return;
    }

    try {
      setActivityLoading(true);
      let notifications = [];
      for (const endpoint of ACTIVITY_FEED_ENDPOINTS) {
        const response = await fetch(`${API_BASE}${endpoint}?limit=${UPDATE_FEED_LIMIT}`, {
          headers: { Authorization: `Bearer ${token}` },
        }).catch(() => null);
        if (!response?.ok) continue;
        const payload = await response.json();
        notifications = Array.isArray(payload)
          ? payload
          : Array.isArray(payload?.notifications)
            ? payload.notifications
            : Array.isArray(payload?.data)
              ? payload.data
              : [];
        if (notifications.length) break;
      }
      setActivityRows(
        notifications
          .map(normalizeActivityRow)
          .sort((left, right) => toTimeMs(right.createdAt) - toTimeMs(left.createdAt))
          .slice(0, UPDATE_FEED_LIMIT)
      );
    } catch (error) {
      console.error("Failed to load dashboard activity feed:", error);
      setActivityRows([]);
    } finally {
      setActivityLoading(false);
    }
  }, [canSeeUsers, permissions.viewDashboard, permissions.viewDevices]);

  useEffect(() => {
    fetchActivityFeed();
  }, [fetchActivityFeed]);

  useEffect(() => {
    if (!socket) return undefined;
    const handleNotification = (payload) => {
      const nextRow = normalizeActivityRow(payload);
      setActivityRows((prev) =>
        [nextRow, ...prev.filter((row) => row.id !== nextRow.id)]
          .sort((left, right) => toTimeMs(right.createdAt) - toTimeMs(left.createdAt))
          .slice(0, UPDATE_FEED_LIMIT)
      );
    };
    socket.on("notification", handleNotification);
    return () => socket.off("notification", handleNotification);
  }, [socket]);

  const handleRefresh = useCallback(() => {
    refetchProfile();
    if (permissions.viewDevices) {
      refetchSummary();
      refetchDevices();
    }
    if (permissions.viewDashboard) {
      refetchTrips();
    }
    if (canSeeUsers) {
      refetchUsers();
      refetchLicenses();
      refetchGroups();
    }
    fetchActivityFeed();
  }, [
    canSeeUsers,
    fetchActivityFeed,
    permissions.viewDashboard,
    permissions.viewDevices,
    refetchDevices,
    refetchGroups,
    refetchLicenses,
    refetchProfile,
    refetchSummary,
    refetchTrips,
    refetchUsers,
  ]);

  const totalVehicles = Number(vehicleStats.all || 0);
  const metricCards = Object.entries(STATUS_META).map(([key, meta]) => ({
    key,
    label: meta.label,
    tone: meta.tone,
    icon: meta.icon,
    value: Number(vehicleStats[key] || 0),
    detail:
      key === "all"
        ? "All vehicles"
        : `${totalVehicles ? ((Number(vehicleStats[key] || 0) / totalVehicles) * 100).toFixed(2) : "0.00"}% of total`,
  }));

  const now = new Date();
  const rangeEnd = new Date(now);
  const rangeStart = new Date(now);
  rangeStart.setDate(now.getDate() - 6);

  const anyLoading =
    profileLoading || summaryLoading || devicesLoading || tripsLoading || usersLoading || licensesLoading || groupsLoading;

  const sidebar = (
    <GlobalDashboardSidebar
      items={sidebarItems}
      profileName={profile?.name || profile?.username || "User"}
      roleLabel={getRoleLabel(role)}
      onSupport={isDemoReadOnly ? undefined : () => navigate("/message")}
      onLogout={() => dispatch(logout())}
    />
  );

  return (
    <Box className="dashboard-page-shell">
      {sidebar}

      <main className="dashboard-content">
        {anyLoading ? <LinearProgress sx={{ borderRadius: 999 }} /> : null}

        <section className="dashboard-hero">
          <div>
            <Typography variant="h3" className="dashboard-hero__title">
              Dashboard
            </Typography>
            <Typography className="dashboard-hero__subtitle">
              {selectedVehicleOption
                ? `Real-time overview for ${selectedVehicleOption.label}`
                : "Real-time overview of your fleet and operations"}
            </Typography>
            <CreditBalanceIndicator />
            {isDemoReadOnly ? (
              <Alert
                severity="warning"
                icon={<WarningAmberRoundedIcon fontSize="inherit" />}
                sx={{
                  mt: 2,
                  borderRadius: 3,
                  border: "1px solid rgba(245, 158, 11, 0.22)",
                  background: "linear-gradient(135deg, rgba(255, 247, 237, 0.98) 0%, rgba(255,255,255,0.98) 100%)",
                  boxShadow: "0 18px 40px rgba(15, 23, 42, 0.08)",
                  alignItems: "flex-start",
                  "& .MuiAlert-message": {
                    width: "100%",
                  },
                }}
              >
                <Typography sx={{ fontSize: "0.82rem", fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase", color: "#b45309" }}>
                  Guided Demo Mode
                </Typography>
                <Typography sx={{ mt: 0.5, fontSize: "1rem", fontWeight: 700, color: "#111827" }}>
                  Experience the platform live, while admin controls stay safely locked.
                </Typography>
                <Typography sx={{ mt: 0.75, fontSize: "0.95rem", lineHeight: 1.7, color: "#4b5563" }}>
                  {demoReadOnlyMessage}
                </Typography>
              </Alert>
            ) : null}
          </div>

          <div className="dashboard-hero__actions">
            <Autocomplete
              className="dashboard-vehicle-search"
              options={vehicleOptions}
              value={selectedVehicleOption}
              inputValue={vehicleSearchInput}
              autoHighlight
              clearOnEscape
              handleHomeEndKeys
              openOnFocus
              selectOnFocus
              noOptionsText={vehicleSearchInput ? "No matching vehicle found" : "Type to search vehicles"}
              onInputChange={(_event, value, reason) => {
                setVehicleSearchInput(value);
                if (reason === "clear") {
                  setSelectedVehicleId("");
                }
              }}
              onChange={(_event, option) => {
                setSelectedVehicleId(option?.id || "");
                setVehicleSearchInput(option?.label || "");
              }}
              getOptionLabel={(option) => (typeof option === "string" ? option : option?.label || "")}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              filterOptions={(options, state) => {
                const query = String(state.inputValue || "").trim().toLowerCase();
                if (!query) return options.slice(0, 20);
                return options
                  .filter((option) => option.searchText.includes(query))
                  .slice(0, 20);
              }}
              clearOnBlur={false}
              renderOption={(props, option) => {
                const { key, ...optionProps } = props;
                return (
                  <li key={key} {...optionProps}>
                    <div className="dashboard-vehicle-search__option">
                      <strong>{option.vehicleNumber || option.label}</strong>
                      <span>{option.name || option.imei || option.label}</span>
                    </div>
                  </li>
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Search vehicle..."
                  size="small"
                  helperText={selectedVehicleOption ? `Showing dashboard for ${selectedVehicleOption.label}` : "Type vehicle number, name, or IMEI"}
                />
              )}
            />

            <div className="dashboard-date-pill">
              <CalendarMonthOutlinedIcon fontSize="small" />
              <span>{formatDateRange(rangeStart, rangeEnd)}</span>
            </div>

            <Button
              variant="contained"
              startIcon={<DownloadRoundedIcon />}
              onClick={() => navigate("/reports")}
              className="dashboard-export-button"
            >
              Export Report
            </Button>

            <IconButton
              onClick={handleRefresh}
              sx={{
                width: 46,
                height: 46,
                bgcolor: "rgba(255,255,255,0.92)",
                border: "1px solid rgba(203,213,225,0.9)",
                boxShadow: "0 10px 28px rgba(15,23,42,0.05)",
                "&:hover": { bgcolor: "white" },
              }}
            >
              <RefreshRoundedIcon fontSize="small" />
            </IconButton>
          </div>
        </section>

        <section className="dashboard-status-grid">
          {(summaryLoading || devicesLoading ? metricCards : metricCards).map((item, index) =>
            summaryLoading || devicesLoading ? (
              <Skeleton key={item.key} variant="rounded" height={88} sx={{ borderRadius: "16px" }} />
            ) : (
              <DashboardStatusCard
                key={item.key}
                label={item.label}
                value={item.value}
                detail={item.detail}
                tone={item.tone}
                icon={item.icon}
                isFirst={index === 0}
                isLast={index === metricCards.length - 1}
                onClick={() => {
                  if (item.key === "all") {
                    navigateToAdminDashboard(navigate, { tab: "map", mapStatusFilter: "all" });
                    return;
                  }
                  const statusMap = { noReporting: "inactive", noData: "no_data", idling: "idle" };
                  navigateToAdminDashboard(navigate, {
                    tab: "map",
                    mapStatusFilter: statusMap[item.key] || item.key,
                  });
                }}
              />
            )
          )}
        </section>

        <section className="dashboard-main-grid">
          <DashboardSection
            title="Live Vehicle Status"
            action={
              <Button size="small" onClick={() => navigateToAdminDashboard(navigate, { tab: "map" })}>
                View All
              </Button>
            }
            className="dashboard-section--status"
          >
            {devicesLoading ? (
              <Skeleton variant="rounded" height={300} />
            ) : vehiclePieData.length ? (
              <div className="dashboard-donut-layout">
                <div className="dashboard-donut-chart">
                  <ResponsiveContainer width="100%" height={260}>
                    <PieChart>
                      <Pie
                        data={vehiclePieData}
                        dataKey="value"
                        innerRadius={58}
                        outerRadius={96}
                        paddingAngle={2}
                        strokeWidth={0}
                      >
                        {vehiclePieData.map((entry) => (
                          <Cell key={entry.key} fill={entry.color} />
                        ))}
                      </Pie>
                      <ReTooltip />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="dashboard-donut-center">
                    <strong>{formatCount(vehicleStats.all)}</strong>
                    <span>Total</span>
                  </div>
                </div>

                <div className="dashboard-donut-legend">
                  {vehiclePieData.map((item) => (
                    <div className="dashboard-donut-legend__row" key={item.key}>
                      <div className="dashboard-donut-legend__label">
                        <span className="dashboard-donut-legend__dot" style={{ backgroundColor: item.color }} />
                        <span>{item.name}</span>
                      </div>
                      <div className="dashboard-donut-legend__value">
                        {`${totalVehicles ? ((item.value / totalVehicles) * 100).toFixed(2) : "0.00"}%`}
                        <span>{`(${formatCount(item.value)})`}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <EmptyCardState title="No vehicle data" description="Vehicle status data is not available yet." />
            )}
          </DashboardSection>

          <DashboardSection title="Today's Overview" className="dashboard-section--overview">
            <div className="dashboard-mini-grid">
              <DashboardTile
                tone="info"
                icon={<RouteOutlinedIcon fontSize="small" />}
                label="On Trip"
                value={formatCount(tripStatus.onTrip)}
              />
              <DashboardTile
                tone="neutral"
                icon={<DirectionsCarFilledOutlinedIcon fontSize="small" />}
                label="No Trip"
                value={formatCount(tripStatus.noTrip)}
              />
              <DashboardTile
                tone="success"
                icon={<CheckCircleOutlineRoundedIcon fontSize="small" />}
                label="Running"
                value={formatCount(vehicleStats.running)}
              />
              <DashboardTile
                tone="danger"
                icon={<ErrorOutlineRoundedIcon fontSize="small" />}
                label="Stopped"
                value={formatCount(vehicleStats.stopped)}
              />
            </div>
          </DashboardSection>

          <DashboardSection
            title="Latest Updates"
            action={
              <Button size="small" onClick={() => navigate("/notifications")}>
                View All
              </Button>
            }
            className="dashboard-section--updates"
          >
            {activityLoading ? (
              <Stack spacing={1.5}>
                <Skeleton variant="rounded" height={92} />
                <Skeleton variant="rounded" height={92} />
              </Stack>
            ) : activityRows.length ? (
              <div className="dashboard-update-list">
                {activityRows.slice(0, 4).map((row) => (
                  <div className="dashboard-update-item" key={row.id}>
                    <Avatar className="dashboard-update-item__avatar">
                      {row.actorName.charAt(0).toUpperCase()}
                    </Avatar>
                    <div className="dashboard-update-item__content">
                      <div className="dashboard-update-item__top">
                        <strong>{row.actorName}</strong>
                        <Chip label={row.actionText} size="small" color="primary" variant="outlined" />
                      </div>
                      <p>{row.message}</p>
                      <div className="dashboard-update-item__meta">
                        <span>{row.itemName}</span>
                        <span>{row.relativeTime}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <Alert severity="info" variant="outlined" sx={{ borderRadius: 3 }}>
                No recent update activity is available right now.
              </Alert>
            )}
          </DashboardSection>
        </section>

        <section className="dashboard-secondary-grid">
          <DashboardSection
            title="Last 7 Days Overview"
            action={
              <div className="dashboard-pill-group">
                {[
                  { key: "distance", label: "Distance" },
                  { key: "trips", label: "Trips" },
                  { key: "fuel", label: "Fuel" },
                ].map((item) => (
                  <button
                    key={item.key}
                    type="button"
                    className={`dashboard-pill ${chartMode === item.key ? "is-active" : ""}`}
                    onClick={() => setChartMode(item.key)}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            }
            className="dashboard-section--chart"
          >
            {devicesLoading || tripsLoading ? (
              <Skeleton variant="rounded" height={300} />
            ) : chartMode === "fuel" ? (
              <EmptyCardState
                title="Fuel analytics unavailable"
                description="No live fuel analytics endpoint is currently wired into this dashboard."
              />
            ) : (
              <div className="dashboard-line-chart">
                <ResponsiveContainer width="100%" height={290}>
                  <LineChart data={overviewSeries} margin={{ top: 12, right: 8, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#edf2f7" />
                    <XAxis dataKey="label" tick={{ fontSize: 12, fill: "#64748b" }} />
                    <YAxis tick={{ fontSize: 12, fill: "#64748b" }} />
                    <ReTooltip />
                    {(chartMode === "distance" || chartMode === "trips") && (
                      <>
                        <Line
                          type="monotone"
                          dataKey="distance"
                          stroke="#2563eb"
                          strokeWidth={3}
                          dot={{ r: 4, fill: "#2563eb" }}
                          name="Total Distance"
                        />
                        <Line
                          type="monotone"
                          dataKey="trips"
                          stroke="#22c55e"
                          strokeWidth={3}
                          dot={{ r: 4, fill: "#22c55e" }}
                          name="Total Trips"
                        />
                      </>
                    )}
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </DashboardSection>

          <DashboardSection
            title="Odometer Comparison"
            action={
              <Button size="small" onClick={() => navigateToAdminDashboard(navigate, { tab: "devices" })}>
                View Vehicles
              </Button>
            }
            className="dashboard-section--odometer"
          >
            {devicesLoading ? (
              <Skeleton variant="rounded" height={320} />
            ) : topOdometerDevices.length ? (
              <Box sx={{ width: "100%", height: 320, minWidth: 0 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={topOdometerDevices}
                    layout="vertical"
                    margin={{ top: 4, right: 18, left: 0, bottom: 4 }}
                    barCategoryGap="24%"
                  >
                    <defs>
                      <linearGradient id="odometerBarGradient" x1="0" y1="0" x2="1" y2="0">
                        <stop offset="0%" stopColor="#2563eb" />
                        <stop offset="100%" stopColor="#38bdf8" />
                      </linearGradient>
                    </defs>
                    <CartesianGrid horizontal={false} strokeDasharray="3 3" stroke="#e8eef6" />
                    <XAxis
                      type="number"
                      tick={{ fontSize: 11, fill: "#64748b" }}
                      tickLine={false}
                      axisLine={false}
                      tickFormatter={(value) => `${formatCompactNumber(value)} km`}
                    />
                    <YAxis
                      type="category"
                      dataKey="label"
                      width={104}
                      tick={{ fontSize: 11, fill: "#334155", fontWeight: 600 }}
                      tickLine={false}
                      axisLine={false}
                      tickFormatter={(value) =>
                        String(value || "").length > 14
                          ? `${String(value).slice(0, 12)}…`
                          : String(value || "")
                      }
                    />
                    <ReTooltip
                      cursor={{ fill: "rgba(37, 99, 235, 0.05)" }}
                      formatter={(value) => [`${formatNumber(value)} km`, "Odometer"]}
                    />
                    <Bar
                      dataKey="value"
                      fill="url(#odometerBarGradient)"
                      radius={[0, 8, 8, 0]}
                      maxBarSize={22}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            ) : (
              <EmptyCardState title="No odometer data" description="Vehicles have no odometer readings yet." />
            )}
          </DashboardSection>

          <DashboardSection title="Vehicle Trip Status" className="dashboard-section--trip-status">
            {tripsLoading ? (
              <Skeleton variant="rounded" height={280} />
            ) : (
              <div className="dashboard-trip-status">
                <div className="dashboard-trip-status__stats">
                  <div>
                    <strong>{formatCount(tripStatus.onTrip)}</strong>
                    <span>On Trip</span>
                  </div>
                  <Divider orientation="vertical" flexItem />
                  <div>
                    <strong>{formatCount(tripStatus.noTrip)}</strong>
                    <span>No Trip</span>
                  </div>
                </div>
                <div className="dashboard-trip-status__visual">
                  <div className="dashboard-trip-status__line" />
                  <LocalShippingOutlinedIcon />
                  <div className="dashboard-trip-status__line" />
                </div>
              </div>
            )}
          </DashboardSection>
        </section>

        <section className="dashboard-bottom-grid">
          {[
            {
              key: "groups",
              icon: <Groups2OutlinedIcon fontSize="small" />,
              label: "Organization",
              value: canSeeUsers ? formatCount(groups.length) : "—",
              caption: "Total Organization",
              tone: "info",
            },
            {
              key: "users",
              icon: <PersonOutlineRoundedIcon fontSize="small" />,
              label: "Users",
              value: canSeeUsers ? formatCount(users.length) : "—",
              caption: "Total Users",
              tone: "success",
            },
            {
              key: "roles",
              icon: <Groups2OutlinedIcon fontSize="small" />,
              label: "Roles",
              value: canSeeUsers ? formatCount(roleCounts.size) : "—",
              caption: "Total Roles",
              tone: "danger",
            },
            {
              key: "purchased",
              icon: <AssessmentOutlinedIcon fontSize="small" />,
              label: "Licenses",
              value: canSeeUsers ? formatCount(licenseSummary.purchased) : "—",
              caption: "Purchased",
              tone: "muted",
            },
            {
              key: "used",
              icon: <WarningAmberRoundedIcon fontSize="small" />,
              label: "Used Licenses",
              value: canSeeUsers ? formatCount(licenseSummary.used) : "—",
              caption: "In Use",
              tone: "warning",
            },
            {
              key: "remaining",
              icon: <SpeedOutlinedIcon fontSize="small" />,
              label: "Remaining Licenses",
              value: canSeeUsers ? formatCount(licenseSummary.remaining) : "—",
              caption: "Available",
              tone: "info",
            },
          ].map((item) => (
            <Paper key={item.key} className="dashboard-bottom-card" elevation={0}>
              {canSeeUsers && (usersLoading || licensesLoading || groupsLoading) ? (
                <Skeleton variant="rounded" height={84} />
              ) : (
                <>
                  <div className={`dashboard-bottom-card__icon tone-${item.tone}`}>{item.icon}</div>
                  <div>
                    <div className="dashboard-bottom-card__label">{item.label}</div>
                    <div className="dashboard-bottom-card__value">{item.value}</div>
                    <div className="dashboard-bottom-card__caption">{item.caption}</div>
                  </div>
                </>
              )}
            </Paper>
          ))}
        </section>

        {anyLoading && isLgDown ? (
          <div className="dashboard-floating-loader">
            <CircularProgress size={20} />
            <span>Refreshing dashboard...</span>
          </div>
        ) : null}
      </main>
    </Box>
  );
}
